import { L } from '@/i18n';

/** Thin client for Studio's own /api back end (same origin, session cookie). */

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, readonly data: Record<string, unknown> = {}) {
    super(code);
  }
}

export async function api<T>(method: 'GET' | 'POST' | 'PUT', path: string, body?: unknown, opts: { keepalive?: boolean; signal?: AbortSignal } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      keepalive: opts.keepalive,
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'offline');
  }
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    /* non-JSON (e.g. the static host answering in local dev) */
    if (!res.ok || res.headers.get('Content-Type')?.includes('text/html')) throw new ApiError(res.ok ? 503 : res.status, 'accounts_unavailable');
  }
  if (!res.ok) throw new ApiError(res.status, String(data.error ?? 'server_error'), data);
  return data as T;
}

/** Human-readable message for an API error code, in the active language. */
export function apiMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return (e as Error)?.message ?? String(e);
  const minutes = Math.ceil(Number(e.data.retryAfterSeconds ?? 60) / 60);
  switch (e.code) {
    case 'offline': return L('No connection. Check your internet and try again.', 'Ingen tilkobling. Sjekk internett og prøv igjen.');
    case 'accounts_unavailable': return L('Accounts are not available on this copy of Studio right now.', 'Kontoer er ikke tilgjengelige i denne versjonen av Studio akkurat nå.');
    case 'invalid_credentials': return L('Wrong username or password.', 'Feil brukernavn eller passord.');
    case 'invalid_recovery': return L('That username and recovery code do not match.', 'Brukernavnet og gjenopprettingskoden passer ikke sammen.');
    case 'too_many_attempts': return L(`Too many attempts. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`, `For mange forsøk. Prøv igjen om ${minutes} minutt${minutes === 1 ? '' : 'er'}.`);
    case 'username_taken': return L('That username is taken. Try another one.', 'Brukernavnet er tatt. Prøv et annet.');
    case 'invalid_username': return L('Usernames are 3–24 characters: letters, numbers, dot, dash or underscore, starting with a letter or number.', 'Brukernavn har 3–24 tegn: bokstaver, tall, punktum, bindestrek eller understrek, og starter med en bokstav eller et tall.');
    case 'password_too_short': return L('Use at least 8 characters for the password.', 'Bruk minst 8 tegn i passordet.');
    case 'password_too_long': return L('That password is too long (max 200 characters).', 'Passordet er for langt (maks 200 tegn).');
    case 'password_too_common': return L('That password is too easy to guess. Try a few words together, like "blue-boat-mountain".', 'Det passordet er for lett å gjette. Prøv noen ord sammen, som «blå-båt-fjell».');
    case 'missing_fields': return L('Fill in every field.', 'Fyll ut alle feltene.');
    case 'bad_password_key': return L('Your browser could not prepare the password. Try updating the browser.', 'Nettleseren klarte ikke å klargjøre passordet. Prøv å oppdatere nettleseren.');
    case 'not_signed_in': return L('You are not logged in any more. Log in again.', 'Du er ikke logget inn lenger. Logg inn på nytt.');
    case 'too_large': return L('Your progress is too large to save to the account.', 'Fremgangen din er for stor til å lagres på kontoen.');
    default: return L(`Something went wrong (${e.code}). Try again.`, `Noe gikk galt (${e.code}). Prøv igjen.`);
  }
}
